import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Stack } from "@mui/material";

import { useGetAllStationDisplays, usePutStationDisplaySortOrder } from "../../hooks";
import { useDisplaysContext } from "./DisplayContextProvider";
import { getUserPermissionById } from "../../redux";
import { PERMISSION_LEVEL_KEYS, PERMISSIONS_KEY } from "../../constants/permissions";
import { StyledNavStack, StyledNavButton } from "../../components/NavBars/SideNavBar/styles";

export const DisplaysMenu = ({ canEditDisplays = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { locationId } = useDisplaysContext();

  const { data } = useGetAllStationDisplays(locationId);
  const { mutate: updateDisplaySortOrder } = usePutStationDisplaySortOrder();
  
  const [localOptions, setLocalOptions] = useState([]);

  const options = useMemo(
    () => data?.map((d) => ({ id: d.id, label: d.title, sortOrder: d.sortOrder })) || [],
    [data]
  );

  useEffect(() => {
    setLocalOptions(options);
  }, [options]);

  const query = new URLSearchParams(location.search);
  const property = query.get("display");

  const setValue = (id) => {
    if (!id) return;

    localStorage.setItem("Display-ID", id);
    query.set("display", id);

    navigate({
      pathname: location.pathname,
      search: query.toString(),
    });
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination || result.source.index === result.destination.index) return;

    const reorderedOptions = Array.from(localOptions);
    const [removed] = reorderedOptions.splice(result.source.index, 1);
    reorderedOptions.splice(result.destination.index, 0, removed);
    
    const updatedOptions = reorderedOptions.map((option, index) => ({
      ...option,
      sortOrder: index + 1
    }));
    
    setLocalOptions(updatedOptions);
    
    const displays = updatedOptions.map(option => ({
      id: option.id,
      sortOrder: option.sortOrder
    }));
    
    updateDisplaySortOrder({ displays });
  };

  useEffect(() => {
    if (!localOptions.length) return;

    const defaultId =
      localStorage.getItem("Display-ID") || property || localOptions[0]?.id;

    if (defaultId !== property) setValue(defaultId);
  }, [property, localOptions]);

  const currentValue = Number(property) || localOptions[0]?.id;

  const getButtonProps = (option) => ({
    label: option.label,
    size: "small",
    iconProps: { size: 12 },
    variant: "text",
    active: option.id === currentValue,
    ...(option.icon ? { icon: option.icon } : {}),
  });

  const renderMenuContent = () => (
    <StyledNavStack>
      <Stack gap="5px">
        {localOptions?.map((option, index) => {
          // for non-admins, just render button
          if (!canEditDisplays) {
            return (
              <StyledNavButton
                key={option.id}
                {...getButtonProps(option)}
                onClick={() => setValue(option.id)}
              />
            );
          }

          // for admins, wrap with drag functionality
          return (
            <Draggable 
              key={String(option.id)} 
              draggableId={String(option.id)} 
              index={index}
            >
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  style={{
                    ...provided.draggableProps.style,
                    opacity: snapshot.isDragging ? 0.5 : 1,
                  }}
                  onClick={() => setValue(option.id)}
                >
                  <StyledNavButton
                    {...getButtonProps(option)}
                    sx={{ 
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                  />
                </div>
              )}
            </Draggable>
          );
        })}
      </Stack>
    </StyledNavStack>
  );

  // conditionally wrap with drag context for admins
  if (canEditDisplays) {
    return (
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId="displays-menu">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {renderMenuContent()}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  }

  return renderMenuContent();
};

const mapStateToProps = (state) => ({
  canEditDisplays: getUserPermissionById(
    state,
    PERMISSIONS_KEY.DISPLAYS,
    PERMISSION_LEVEL_KEYS.EDIT
  ),
});

export default connect(mapStateToProps)(DisplaysMenu);
